import { Meeting, meetingService } from "@/api/calendar";
import { AppointmentFormModal } from "@/components/calendar/AppointmentFormModal";
import { CalendarGrid } from "@/components/calendar/CalendarGrid";
import { CalendarHeader } from "@/components/calendar/CalendarHeader";
import { DayView } from "@/components/calendar/DayView";
import { WeekView } from "@/components/calendar/WeekView";
import { EventModal } from "@/components/calendar/EventModal";
import { Main } from '@/components/layout/main'

import { toast } from "@/hooks/use-toast";
import { useCalendar } from "@/hooks/useCalendar";
import { generateMonthCalendar } from "@/utils/calendar";
import { handleServerError } from "@/utils/handle-server-error";
import { Holiday, holidayService } from "@/api/holidays";
import { addDays, subDays } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearch } from "@tanstack/react-router";
import { filterNonNullValues } from "@/lib/utils";
import { getMonthNames } from "@/utils/date-locale";
import { usePermission } from "@/hooks/use-permission";
import { UserPermission } from "@/constants/permissions";

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  // Initialize view from URL or default to "month"
  const [view, setView] = useState<"month" | "week" | "day">(
    search?.view === "day" ? "day" : search?.view === "week" ? "week" : "month"
  );

  // Initialize selectedDay from URL or default to current date
  const initialDate = useMemo(() => {
    // If all year, month, day parameters are present in URL
    if (search?.year && search?.month && search?.day) {
      const year = Number(search.year);
      const month = Number(search.month) - 1; // JS months are 0-based
      const day = Number(search.day);

      // Validate the date to ensure it's not invalid
      const date = new Date(year, month, day);
      if (
        !isNaN(date.getTime()) &&
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        return date;
      }
    }
    return new Date();
  }, [search?.day, search?.month, search?.year]);

  const [selectedDay, setSelectedDay] = useState<Date>(initialDate);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isPostponeMode, setIsPostponeMode] = useState<boolean>(false);

  // Initialize selectedCaseTypes from URL
  const [selectedCaseTypes, setSelectedCaseTypes] = useState<string[]>(
    search?.case_types ? search.case_types : []
  );

  // Initialize selectedAssignedTo from URL
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<string | undefined>(
    search?.assigned_to ? String(search.assigned_to) : undefined
  );

  const isEditMode = useMemo(() => {
    return !!selectedEvent && !!selectedEvent.originalData && !isPostponeMode;
  }, [isPostponeMode, selectedEvent]);

  // Initialize the calendar with the date from URL
  const { currentDate, goToPreviousMonth, goToNextMonth, goToPreviousWeek, goToNextWeek, goToToday, goToDate } =
    useCalendar(initialDate);

  // Get the current month and year for fetching meetings
  const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-based
  const currentYear = currentDate.getFullYear();

  const fetch_calendar = useCallback((month: number, year: number, caseTypes: string[] = [], assignedTo?: string) => {
    setLoading(true);
    meetingService.getMeetings(month, year, undefined, caseTypes, assignedTo)
      .then((resp) => {
        setMeetings(resp)
        setLoading(false)
      }).catch((_) => {
        setLoading(false)
      });

    holidayService.getHolidays()
      .then((resp) => {
        setHolidays(resp.holidays);
      }).catch((e) => {
        console.error("Failed to fetch holidays:", e);
      });
  }, []);

  useEffect(() => {
    fetch_calendar(currentMonth, currentYear, selectedCaseTypes, selectedAssignedTo);
  }, [currentMonth, currentYear, selectedCaseTypes, selectedAssignedTo, fetch_calendar]);

  const monthCalendar = useMemo(() => {
    return generateMonthCalendar(currentDate, meetings);
  }, [currentDate, meetings]);

  const today = useMemo(() => new Date(), []);

  const goToPreviousDay = useCallback(() => {
    setSelectedDay((prevDate) => {
      const newDate = subDays(prevDate, 1);
      if (newDate.getMonth() !== currentDate.getMonth()) {
        goToDate(newDate);
      }
      return newDate;
    });
  }, [currentDate, goToDate]);

  const goToNextDay = useCallback(() => {
    setSelectedDay((prevDate) => {
      const newDate = addDays(prevDate, 1);
      if (newDate.getMonth() !== currentDate.getMonth()) {
        goToDate(newDate);
      }
      return newDate;
    });
  }, [currentDate, goToDate]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEventClick = useCallback((event: any) => {
    setSelectedEvent(event);
    setShowAppointmentModal(false);
  }, []);

  const handleDeleteMeeting = useCallback(
    async (id: string) => {
      try {
        await meetingService.deleteMeeting(id);
        setSelectedEvent(null);
        setMeetings((prevMeetings) => prevMeetings.filter(meeting => meeting.id !== id));
        toast({
          title: t('appointmentDeleted'),
        })
      } catch (e) {
        handleServerError(e)
      }
    },
    [t]
  );

  const openNewAppointmentModal = useCallback(() => {
    setSelectedDay((current) => {
      const dateToUse = new Date(current);
      dateToUse.setHours(9, 0, 0, 0);
      return dateToUse;
    });
    setSelectedEvent(null);
    setShowAppointmentModal(true);
  }, []);

  const handleSaveMeeting = useCallback(
    (meeting?: Meeting) => {
      if (meeting) {
        if (isEditMode) {
          // Update existing meeting in the list
          setMeetings((prevMeetings) =>
            prevMeetings.map((m) => (m.id === meeting.id ? meeting : m))
          );
        } else if (isPostponeMode) {
          // Handle postponed meeting - need to update both the original and new meetings
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const originalMeetingId = (meeting as any)._originalMeetingId;

          setMeetings((prevMeetings) => {
            // Create a copy of the meetings array to work with
            const updatedMeetings = [...prevMeetings];

            // Find and update the original meeting to mark it as postponed
            const originalMeetingIndex = updatedMeetings.findIndex(m => m.id === originalMeetingId);
            if (originalMeetingIndex !== -1) {
              updatedMeetings[originalMeetingIndex] = {
                ...updatedMeetings[originalMeetingIndex],
                is_postponed: true
              };
            }

            // Add the new meeting (it might not already be in the list)
            // Remove the temporary _originalMeetingId field
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { _originalMeetingId, ...cleanMeeting } = meeting as any;
            const newMeetingIndex = updatedMeetings.findIndex(m => m.id === meeting.id);
            if (newMeetingIndex !== -1) {
              updatedMeetings[newMeetingIndex] = cleanMeeting;
            } else {
              updatedMeetings.push(cleanMeeting);
            }

            return updatedMeetings;
          });
        } else {
          // Add new meeting to the list
          setMeetings((prevMeetings) => [...prevMeetings, meeting]);
        }
      } else {
        // Refresh all meetings if no specific meeting was returned
        fetch_calendar(currentMonth, currentYear, selectedCaseTypes, selectedAssignedTo);
      }
    },
    [currentMonth, currentYear, isEditMode, isPostponeMode, fetch_calendar, selectedCaseTypes, selectedAssignedTo]
  );

  const { can } = usePermission();
  const handleCellAppointment = useCallback((date: Date | null) => {
    // Only allow users with permission to create appointments
    if (date && can(UserPermission.ADD_RENDEZVOUS)) {
      setSelectedDay(date);
      setSelectedEvent(null);
      setShowAppointmentModal(true);
    }
  }, [can]);

  const handleEditEvent = useCallback(() => {
    if (selectedEvent?.originalData) {
      if (selectedEvent.originalData.start_time) {
        const meetingDate = new Date(selectedEvent.originalData.start_time * 1000);
        setSelectedDay(meetingDate);
      }
      setShowAppointmentModal(true);
    }
  }, [selectedEvent]);

  const handlePostponeMeeting = useCallback(() => {
    if (selectedEvent?.originalData) {
      setIsPostponeMode(true);
      if (selectedEvent.originalData.start_time) {
        const meetingDate = new Date(selectedEvent.originalData.start_time * 1000);
        setSelectedDay(meetingDate);
      }
      setShowAppointmentModal(true);
    }
  }, [selectedEvent]);

  const handleViewChange = useCallback(
    (newView: "month" | "week" | "day") => {
      setView(newView);


      if ((newView === "day" || newView === "week") && view === "month") {
        if (selectedDay.getMonth() !== currentDate.getMonth() ||
          selectedDay.getFullYear() !== currentDate.getFullYear()) {
          goToDate(selectedDay);
        }
      }
    },
    [currentDate, goToDate, selectedDay, view]
  );

  const handleGoToToday = useCallback(() => {
    const today = new Date();
    goToToday();
    setSelectedDay(today);
    // Keep the current view as is - don't change from month to day or vice versa
  }, [goToToday]);

  const handleModalOpenChange = useCallback(
    (open: boolean) => {
      setShowAppointmentModal(open);
      if (!open) {
        if (!isEditMode && !isPostponeMode) {
          setSelectedEvent(null);
        }
        setIsPostponeMode(false);
      }
    },
    [isEditMode, isPostponeMode]
  );

  const handleCloseEventModal = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  // Format month display for header
  const monthDisplay = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthNames = getMonthNames(i18n.language as 'ar' | 'fr');
    return `${monthNames[month]} ${year}`;
  }, [currentDate, i18n.language]);

  // Handle clicking on a day number in the month view
  const handleDayNumberClick = useCallback((date: Date) => {
    setSelectedDay(date);
    setView("day");

    // If the clicked date is not in the current month, update the calendar
    if (date.getMonth() !== currentDate.getMonth() ||
      date.getFullYear() !== currentDate.getFullYear()) {
      goToDate(date);
    }
  }, [currentDate, goToDate]);

  const handleCaseTypeFilterChange = useCallback((values: string[]) => {
    setSelectedCaseTypes(values);
  }, []);

  const handleAssignedToFilterChange = useCallback((value: string | undefined) => {
    setSelectedAssignedTo(value);
  }, []);

  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const day = selectedDay.getDate();

    navigate({
      to: ".",
      search: filterNonNullValues({
        case_types: selectedCaseTypes.length > 0 ? selectedCaseTypes : null,
        assigned_to: selectedAssignedTo ?? null,
        view,
        year,
        month,
        day: view === 'day' ? day : null,
      }),
      replace: true,
    });
  }, [view, currentDate, selectedDay, selectedCaseTypes, selectedAssignedTo, navigate]);

  return (
    <>

      <Main className='flex flex-col h-full'>
        <div className="flex-shrink-0">
          <CalendarHeader
            view={view}
            setView={handleViewChange}
            selectedDay={selectedDay}
            monthDisplay={monthDisplay}
            currentDate={currentDate}
            goToToday={handleGoToToday}
            goToPreviousMonth={goToPreviousMonth}
            goToNextMonth={goToNextMonth}
            goToPreviousWeek={goToPreviousWeek}
            goToNextWeek={goToNextWeek}
            goToPreviousDay={goToPreviousDay}
            goToNextDay={goToNextDay}
            goToDate={goToDate}
            selectedCaseTypes={selectedCaseTypes}
            onCaseTypeFilterChange={handleCaseTypeFilterChange}
            selectedAssignedTo={selectedAssignedTo}
            onAssignedToFilterChange={handleAssignedToFilterChange}
          />
        </div>

        <div className="flex-1 overflow-auto px-4 sm:px-6">
          {view === "month" ? (
            <CalendarGrid
              isLoading={loading}
              monthCalendar={monthCalendar}
              today={today}
              onEventClick={handleEventClick}
              onSelectionComplete={() => { }}
              onCellAppointment={handleCellAppointment}
              onDayNumberClick={handleDayNumberClick}
              holidays={holidays}
            />
          ) : view === "week" ? (
            <WeekView
              date={currentDate}
              meetings={meetings}
              holidays={holidays}
              onEventClick={handleEventClick}
              onAddAppointment={openNewAppointmentModal}
              onDayClick={handleDayNumberClick}
            />
          ) : (
            <DayView
              date={selectedDay}
              meetings={meetings}
              onEventClick={handleEventClick}
              onAddAppointment={openNewAppointmentModal}
            />
          )}
        </div>
      </Main>

      {selectedEvent && !showAppointmentModal && (
        <EventModal
          event={selectedEvent}
          onClose={handleCloseEventModal}
          onEdit={handleEditEvent}
          onDelete={handleDeleteMeeting}
          onPostpone={handlePostponeMeeting}
        />
      )}

      <AppointmentFormModal
        open={showAppointmentModal}
        onOpenChange={handleModalOpenChange}
        selectedDate={selectedDay}
        onSubmit={handleSaveMeeting}
        editMode={isEditMode}
        postponeMode={isPostponeMode}
        meetingToEdit={isEditMode || isPostponeMode ? selectedEvent?.originalData : undefined}
        onUpdate={() => {
          setSelectedEvent(null);
        }}
      />
    </>
  );
}
